type RuntimeWindow = Window & typeof globalThis & {
  SAL_clearStorage?: (callback?: () => void) => void
  SAL_setStorage?: (key: string, data: string, callback?: () => void) => void
  SAL_getStorage?: (key: string, callback?: (data: string) => void) => void
  SAL_removeStorage?: (key: string, callback?: () => void) => void
  SAL_getStorageInfo?: (callback?: (names: string[]) => void) => void
}

type OriginalHooks = {
  clearStorage?: RuntimeWindow['SAL_clearStorage']
  setStorage?: RuntimeWindow['SAL_setStorage']
  getStorage?: RuntimeWindow['SAL_getStorage']
  removeStorage?: RuntimeWindow['SAL_removeStorage']
  getStorageInfo?: RuntimeWindow['SAL_getStorageInfo']
}

type SaveProxyOptions = {
  saveStorageKey: string
}

type StorageRecord = {
  id: string
  namespace: string
  key: string
  value: string
  updatedAt: number
}

const DB_NAME = 'avgmaker-preview-test-save'
const DB_VERSION = 1
const STORE_NAME = 'storage'
const INDEX_NAMESPACE = 'namespace'

let restoreHooks: (() => void) | null = null
let databasePromise: Promise<IDBDatabase> | null = null
const storageValueCache = new Map<string, string>()

function normalizePath(value: string): string {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .trim()
}

function getRecordId(namespace: string, key: string): string {
  return `${namespace}\n${String(key || '')}`
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 请求失败'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB 事务失败'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB 事务中断'))
  })
}

async function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      const store = db.objectStoreNames.contains(STORE_NAME)
        ? request.transaction!.objectStore(STORE_NAME)
        : db.createObjectStore(STORE_NAME, { keyPath: 'id' })
      if (!store.indexNames.contains(INDEX_NAMESPACE)) {
        store.createIndex(INDEX_NAMESPACE, 'namespace', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB 打开失败'))
  })
  return databasePromise
}

async function getStorage(namespace: string, key: string): Promise<string> {
  const cacheKey = getRecordId(namespace, key)
  if (storageValueCache.has(cacheKey)) return storageValueCache.get(cacheKey) ?? ''
  const db = await openDatabase()
  const transaction = db.transaction(STORE_NAME, 'readonly')
  const store = transaction.objectStore(STORE_NAME)
  const record = await requestToPromise<StorageRecord | undefined>(store.get(cacheKey))
  const value = record?.value ?? ''
  if (record) storageValueCache.set(cacheKey, value)
  return value
}

async function setStorage(namespace: string, key: string, value: string): Promise<void> {
  const cacheKey = getRecordId(namespace, key)
  storageValueCache.set(cacheKey, value)
  const db = await openDatabase()
  const transaction = db.transaction(STORE_NAME, 'readwrite')
  const store = transaction.objectStore(STORE_NAME)
  store.put({
    id: cacheKey,
    namespace,
    key,
    value,
    updatedAt: Date.now(),
  } satisfies StorageRecord)
  await transactionDone(transaction)
}

async function removeStorage(namespace: string, key: string): Promise<void> {
  const cacheKey = getRecordId(namespace, key)
  storageValueCache.delete(cacheKey)
  const db = await openDatabase()
  const transaction = db.transaction(STORE_NAME, 'readwrite')
  transaction.objectStore(STORE_NAME).delete(cacheKey)
  await transactionDone(transaction)
}

async function listStorageKeys(namespace: string): Promise<string[]> {
  const db = await openDatabase()
  const transaction = db.transaction(STORE_NAME, 'readonly')
  const index = transaction.objectStore(STORE_NAME).index(INDEX_NAMESPACE)
  const records = await requestToPromise<StorageRecord[]>(index.getAll(namespace))
  return records.map((record) => record.key).filter(Boolean).sort()
}

async function clearStorage(namespace: string): Promise<void> {
  const db = await openDatabase()
  const keys = await listStorageKeys(namespace)
  const transaction = db.transaction(STORE_NAME, 'readwrite')
  const store = transaction.objectStore(STORE_NAME)
  for (const key of keys) {
    storageValueCache.delete(getRecordId(namespace, key))
    store.delete(getRecordId(namespace, key))
  }
  await transactionDone(transaction)
}

function installBrowserStorageBridge(
  runtimeWindow: RuntimeWindow,
  namespace: string,
): Pick<OriginalHooks, 'clearStorage' | 'setStorage' | 'getStorage' | 'removeStorage' | 'getStorageInfo'> {
  const originals = {
    clearStorage: runtimeWindow.SAL_clearStorage,
    setStorage: runtimeWindow.SAL_setStorage,
    getStorage: runtimeWindow.SAL_getStorage,
    removeStorage: runtimeWindow.SAL_removeStorage,
    getStorageInfo: runtimeWindow.SAL_getStorageInfo,
  }

  runtimeWindow.SAL_clearStorage = (callback?: () => void) => {
    void (async () => {
      try {
        await clearStorage(namespace)
      } catch {
      } finally {
        callback?.()
      }
    })()
  }

  runtimeWindow.SAL_setStorage = (key: string, data: string, callback?: () => void) => {
    const storageKey = String(key || '')
    const text = String(data ?? '')
    void (async () => {
      try {
        await setStorage(namespace, storageKey, text)
      } catch {
      } finally {
        callback?.()
      }
    })()
  }

  runtimeWindow.SAL_getStorage = (key: string, callback?: (data: string) => void) => {
    const storageKey = String(key || '')
    void (async () => {
      try {
        callback?.(await getStorage(namespace, storageKey))
      } catch {
        callback?.('')
      }
    })()
  }

  runtimeWindow.SAL_removeStorage = (key: string, callback?: () => void) => {
    const storageKey = String(key || '')
    void (async () => {
      try {
        await removeStorage(namespace, storageKey)
      } catch {
      } finally {
        callback?.()
      }
    })()
  }

  runtimeWindow.SAL_getStorageInfo = (callback?: (names: string[]) => void) => {
    void (async () => {
      try {
        callback?.(await listStorageKeys(namespace))
      } catch {
        callback?.([])
      }
    })()
  }

  return originals
}

/**
 * 将测试运行时的 SAL_*Storage 存档接口代理到 IndexedDB。
 * @param runtimeWindow 测试 iframe 的运行时 window
 * @param options 存档命名空间配置，通常为 `{guid}/current`
 */
export function installIndexedDbSaveProxy(runtimeWindow: RuntimeWindow, options: SaveProxyOptions): void {
  if (restoreHooks) {
    restoreHooks()
    restoreHooks = null
  }

  const namespace = normalizePath(options.saveStorageKey)
  if (!namespace) {
    throw new Error('测试存档命名空间为空')
  }
  const originals = installBrowserStorageBridge(runtimeWindow, namespace)

  restoreHooks = () => {
    runtimeWindow.SAL_clearStorage = originals.clearStorage
    runtimeWindow.SAL_setStorage = originals.setStorage
    runtimeWindow.SAL_getStorage = originals.getStorage
    runtimeWindow.SAL_removeStorage = originals.removeStorage
    runtimeWindow.SAL_getStorageInfo = originals.getStorageInfo
  }
}

/**
 * 还原测试运行时的存档接口代理。
 */
export function uninstallIndexedDbSaveProxy(): void {
  restoreHooks?.()
  restoreHooks = null
}
